import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Field } from '../../components/ui/Field';
import { api } from '../../lib/api';
import { useSession } from '../../stores/session';

/**
 * Closing the account.
 *
 * ## Three things stand between here and gone
 *
 * A press to reveal it, the account's own name typed out, and the credential
 * that opens the account - a password for the accounts that have one, and a
 * round trip through Discord for the accounts whose credential is Discord.
 * That is more friction than anything else in the product, deliberately: this
 * is the one action with no undo, and the cost of making it slightly annoying
 * is nothing next to the cost of somebody doing it by accident.
 *
 * Typing the username is the part that does the real work. A password field
 * alone is answered on reflex by anybody who has just typed their password
 * somewhere else; typing your own name is a sentence you have to mean.
 *
 * ## It says what actually happens
 *
 * Not "this cannot be undone" and nothing else. People are owed the specifics
 * before they decide - which of their things disappear, which stay because
 * other people are in them, and what happens to the address they signed up
 * with.
 */
/** What the callback's one word means, when it is not "ok". */
const REAUTH_TROUBLE: Record<string, string> = {
  cancelled: 'Nothing was confirmed, and nothing has been deleted.',
  mismatch: 'That is a different Discord account from the one on this profile.',
  failed: 'Discord could not confirm that. Try again in a moment.',
};

export function DeleteAccount() {
  const user = useSession((state) => state.user);
  const logout = useSession((state) => state.logout);
  const navigate = useNavigate();
  const { search } = useLocation();

  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [password, setPassword] = useState('');

  /*
   * Whether there is a password to re-enter at all.
   *
   * An account that arrived through Discord has none, and asking for one it
   * cannot have would be a form that can never be completed. The same endpoint
   * the connections card reads, so this costs no extra request.
   */
  const { data: credentials } = useQuery({
    queryKey: ['me', 'connections'],
    queryFn: () => api.get<{ has_password: boolean; reauth_ready: boolean }>('/me/connections'),
  });

  const hasPassword = credentials?.has_password ?? true;

  /*
   * An account with no password re-proves the credential it does have.
   *
   * The proof is held server-side for five minutes and this only reads whether
   * one is standing - see `Reauth`. It survives the round trip to Discord,
   * which is the point: that trip reloads this page and takes every piece of
   * component state with it.
   */
  const proved = credentials?.reauth_ready ?? false;

  const reauth = useMutation({
    mutationFn: () => api.post<{ url: string }>('/me/reauth/discord'),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  /*
   * Read once and taken out of the address bar, so pressing back later does not
   * bring the notice with it.
   */
  const [outcome, setOutcome] = useState<string | null>(null);

  useEffect(() => {
    const said = new URLSearchParams(search).get('reauth');

    if (said === null) return;

    setOutcome(said);
    navigate('/settings', { replace: true });
  }, [search, navigate]);

  const remove = useMutation({
    // `confirm` is what re-authenticates an account with no password, and the
    // server decides which of the two it requires - see `DeleteAccountAction`.
    mutationFn: () => api.delete('/me', { body: { password, confirm } }),
    onSuccess: () => {
      // The tokens are already revoked server-side; this clears the client so
      // the next render is not a signed-in shell with no account behind it.
      void logout();
      navigate('/');
    },
  });

  const named = user?.username ?? '';
  const ready = confirm.trim() === named && (hasPassword ? password !== '' : proved);

  return (
    <Card className="border-danger/30 p-6">
      <h2 className="mb-1 text-sm font-medium uppercase tracking-wider text-faint">
        Delete account
      </h2>

      {!open ? (
        <>
          <p className="mb-4 text-sm text-soft">
            Closes your account and removes your tracks for good.
          </p>

          <Button variant="secondary" onClick={() => setOpen(true)} data-delete-account>
            Delete my account
          </Button>
        </>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();

            if (ready && !remove.isPending) remove.mutate();
          }}
        >
          <div className="text-sm text-soft">
            <p className="mb-2">This cannot be undone. When you confirm:</p>

            <ul className="ml-4 list-disc space-y-1 text-faint">
              <li>your tracks, their audio and their artwork are deleted</li>
              <li>your playlists and albums are deleted</li>
              <li>your profile stops existing and your name is retired</li>
              <li>
                messages you sent stay in other people’s conversations, shown as
                from a deleted account
              </li>
              <li>your email address is freed, so you could sign up again later</li>
            </ul>
          </div>

          <Field
            label={`Type ${named} to confirm`}
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="off"
            data-delete-confirm
          />

          {hasPassword ? (
            <Field
              label="Your password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              data-delete-password
            />
          ) : (
            /*
              No password to re-enter, so the credential that does open this
              account is the one asked for. The button leaves the site and comes
              back, which is why the proof lives on the server rather than in
              this component.
            */
            <div className="flex flex-col gap-2" data-delete-reauth={proved ? 'done' : 'needed'}>
              {proved ? (
                <Alert tone="success">Confirmed with Discord. This holds for a few minutes.</Alert>
              ) : (
                <>
                  <p className="text-[13px] leading-relaxed text-soft">
                    Your account has no password, so confirm with Discord instead. This opens Discord and
                    comes straight back.
                  </p>

                  <Button
                    type="button"
                    variant="secondary"
                    loading={reauth.isPending}
                    onClick={() => reauth.mutate()}
                    data-delete-confirm-discord
                  >
                    Confirm with Discord
                  </Button>
                </>
              )}
            </div>
          )}

          {outcome !== null && outcome !== 'ok' && (
            <Alert tone="error">{REAUTH_TROUBLE[outcome] ?? REAUTH_TROUBLE.failed}</Alert>
          )}

          {remove.isError && (
            <Alert tone="error">
              {hasPassword
                ? 'That did not go through. Check your password and try again.'
                : 'That did not go through. Confirm with Discord again and retry.'}
            </Alert>
          )}

          <div className="flex items-center gap-2">
            <Button
              type="submit"
              variant="danger"
              disabled={!ready}
              loading={remove.isPending}
              data-delete-submit
            >
              Delete my account
            </Button>

            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setOpen(false);
                setConfirm('');
                setPassword('');
              }}
            >
              Keep it
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
